import { useMemo, useState, type ReactNode } from 'react'

import Alert from '@mui/material/Alert'
import Breadcrumbs from '@mui/material/Breadcrumbs'
import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Grid from '@mui/material/Grid2'
import LinearProgress from '@mui/material/LinearProgress'
import Link from '@mui/material/Link'
import MenuItem from '@mui/material/MenuItem'
import Skeleton from '@mui/material/Skeleton'
import Tab from '@mui/material/Tab'
import Tabs from '@mui/material/Tabs'
import TabContext from '@mui/lab/TabContext'
import TabPanel from '@mui/lab/TabPanel'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, Link as RouterLink, useNavigate } from '@tanstack/react-router'
import { toast } from 'react-toastify'
import { z } from 'zod'

import type { PersonalField } from '@server/domain/due-diligence'

import CustomAvatar from '@core/components/mui/avatar'
import CustomChip from '@core/components/mui/chip'
import CustomTabList from '@core/components/mui/tab-list'
import CustomTextField from '@core/components/mui/text-field'
import MenuButton from '@components/menu-button'
import CaseBudgetLine from '@components/due-diligence/case-budget-line'
import CaseStatusControl from '@components/due-diligence/case-status-control'
import CaseVerdict from '@components/due-diligence/case-verdict'
import CollectionSummary from '@components/due-diligence/collection-summary'
import NextSteps from '@components/due-diligence/next-steps'
import OpinionDialog, { type OpinionUsage } from '@components/due-diligence/opinion-dialog'
import LawyerReviewCard from '@components/due-diligence/lawyer-review-card'
import { hasLinksReport } from '@server/domain/links-report'
import ManualQueuePanel from '@components/due-diligence/manual-queue-panel'
import MatriculaPanel from '@components/due-diligence/matricula-panel'
import CaseTermsCard from '@components/due-diligence/case-terms-card'
import DeliveryCard from '@components/due-diligence/delivery-card'
import DossierReviewCard from '@components/due-diligence/dossier-review-card'
import EvidenceGapsCard from '@components/due-diligence/evidence-gaps-card'
import LegalBasisCard from '@components/due-diligence/legal-basis-card'
import MonitoringPanel from '@components/due-diligence/monitoring-panel'
import PowersPanel from '@components/due-diligence/powers-panel'
import QueryLogCard from '@components/due-diligence/query-log-card'
import DocumentRequestList from '@components/due-diligence/document-request-list'
import DossierDownloadButton from '@components/due-diligence/dossier-download-button'
import RequestDocumentsDialog from '@components/due-diligence/request-documents-dialog'
import WaiverDialog from '@components/due-diligence/waiver-dialog'
import WaiverList from '@components/due-diligence/waiver-list'
import PersonalDataDialog from '@components/due-diligence/personal-data-dialog'
import ScrapingIndicator from '@components/due-diligence/scraping-indicator'
import SubjectDialog from '@components/due-diligence/subject-dialog'
import SubjectPanel from '@components/due-diligence/subject-panel'

import { useTRPC } from '@/lib/trpc'
import { requireSubscription } from '@/lib/route-guards'
import {
  CASE_PHASE,
  casePhaseOf,
  COLLECTION_LOOK,
  COLLECTION_STAGES,
  collectionStageOf,
  collectionSummaryOf,
  formatDate,
  isSearchable,
  nextStepsOf,
  opinionOpeningOf,
  percentOf,
  pluralize,
  statsOf,
  VISUAL_STATUS,
  visualStatusOf,
  type AbsenceStake,
  type CasePhase,
  type Certificate,
  type CollectionStage,
  type NextStepKind,
  type Subject,
  type VisualStatus
} from '@/lib/due-diligence'

/** What the subject dialog offers before the case has loaded: nothing to choose from. */
const NO_ROLES = { cpf: [], cnpj: [], property: [] }

/**
 * The tabs follow the life of the case — collecting, reading, delivering —
 * with what is set once (the terms) and what is only audited (the log) last.
 */
const TABS = ['certificates', 'analysis', 'delivery', 'settings', 'log'] as const

type CaseTab = (typeof TABS)[number]

const VISUAL_STATUSES = Object.keys(VISUAL_STATUS) as [VisualStatus, ...VisualStatus[]]

/** The order the filter chips go in: what needs a person, then what is moving, then what came back. */
const FILTER_ORDER: VisualStatus[] = [
  'missingData',
  'failed',
  'notStarted',
  'manual',
  'running',
  'awaitingEmail',
  'awaitingAi',
  'mirrored',
  'adverse',
  'warning',
  'clear'
]

const searchSchema = z.object({
  tab: z.enum(TABS).optional(),
  /** Which of the four collection stages the list shows — a tile of the header points here. */
  stage: z.enum(['obtained', 'inProgress', 'needsYou', 'noSource']).optional(),
  /** Certificate states shown in the list — a chip or a next step points here. */
  states: z.array(z.enum(VISUAL_STATUSES)).optional(),
  subject: z.string().optional()
})

type CaseSearch = z.infer<typeof searchSchema>

/** The tab a case opens on when the address names none: the one its phase works in. */
const TAB_OF_PHASE: Record<CasePhase, CaseTab> = {
  collecting: 'certificates',
  analyzing: 'analysis',
  releasing: 'delivery',
  closed: 'delivery'
}

const SUBJECT_COUNT_WORDS = {
  cpf: ['pessoa', 'pessoas'],
  cnpj: ['empresa', 'empresas'],
  property: ['imóvel', 'imóveis']
} as const

export const Route = createFileRoute('/_dashboard/diligences/$id')({
  beforeLoad: requireSubscription,
  validateSearch: searchSchema,
  component: DiligenceDetail
})

function DiligenceDetail() {
  const { id } = Route.useParams()
  const search = Route.useSearch()
  const navigate = useNavigate({ from: Route.fullPath })
  const trpc = useTRPC()
  const queryClient = useQueryClient()

  const setSearch = (patch: Partial<CaseSearch>) =>
    void navigate({ search: current => ({ ...current, ...patch }), replace: true })

  /**
   * While the system works the case — a search in flight, a reading, an
   * e-mail awaited, a retry scheduled — the state changes in the database
   * without anybody clicking here. So the screen goes looking, and stops the
   * moment nothing is in progress.
   */
  const { data: diligence, isPending } = useQuery({
    ...trpc.diligences.byId.queryOptions({ id }),
    refetchInterval: query =>
      query.state.data?.subjects.some(subject =>
        subject.certificates.some(
          certificate => certificate.status === 'running' || certificate.collection?.stage === 'inProgress'
        )
      )
        ? 15_000
        : false
  })

  /** The same pace for what is measured off the lines: the light, the desk. */
  const collecting = Boolean(
    diligence?.subjects.some(subject =>
      subject.certificates.some(certificate => certificate.collection?.stage === 'inProgress')
    )
  )

  const [subjectDialogOpen, setSubjectDialogOpen] = useState(false)
  const [opinionOpen, setOpinionOpen] = useState(false)
  const [requestOpen, setRequestOpen] = useState(false)
  const [waiverOpen, setWaiverOpen] = useState(false)
  const [opinion, setOpinion] = useState('')
  /** Which kept version the dialog shows — the newest, unless the person picked another. */
  const [opinionId, setOpinionId] = useState<string | null>(null)
  const [opinionUsage, setOpinionUsage] = useState<OpinionUsage | null>(null)
  const [editing, setEditing] = useState<{ subject: Subject; fields: PersonalField[] } | null>(null)
  const [batch, setBatch] = useState<{ done: number; total: number; current: string } | null>(null)

  const invalidate = () => queryClient.invalidateQueries({ queryKey: trpc.diligences.byId.queryKey({ id }) })

  const updateStatus = useMutation(
    trpc.diligences.update.mutationOptions({
      onSuccess: () => {
        toast.success('Status atualizado.')
        void invalidate()
        void queryClient.invalidateQueries({ queryKey: trpc.diligences.list.queryKey() })
      },
      onError: error => toast.error(error.message)
    })
  )

  const writeOpinion = useMutation(
    trpc.diligences.writeOpinion.mutationOptions({
      onSuccess: async result => {
        setOpinion(result.opinion)
        setOpinionUsage(result.usage)
        setOpinionId(result.id)
        await queryClient.invalidateQueries({ queryKey: trpc.diligences.opinions.queryKey({ id }) })
      },
      onError: error => {
        toast.error(error.message)
        setOpinionOpen(false)
      }
    })
  )

  const checkMailbox = useMutation(
    trpc.diligences.checkMailbox.mutationOptions({
      onSuccess: result => {
        toast.success(
          result.attached > 0
            ? `${pluralize(result.attached, 'PDF anexado', 'PDFs anexados')} de ${result.checked} pendentes.`
            : 'Nenhum PDF novo chegou por e-mail.'
        )
        void invalidate()
      },
      onError: error => toast.error(error.message)
    })
  )

  const reanalyzeCertificate = useMutation(trpc.certificates.reanalyze.mutationOptions())

  /** One call per subject: the server walks each list and skips what has no robot or is missing data. */
  const scrapeSubject = useMutation(trpc.subjects.scrapeAll.mutationOptions())

  const stats = useMemo(() => (diligence ? statsOf(diligence) : null), [diligence])
  const collection = useMemo(() => collectionSummaryOf(diligence?.subjects ?? []), [diligence])

  /** Who is looking — only an administrator may answer for an opinion today. */
  const { data: viewer } = useQuery(trpc.auth.me.queryOptions())

  /**
   * The traffic light, as the server measured it.
   *
   * Never recomputed here: green has to mean "nothing found over everything
   * that was owed", and a screen that can compute it differently will.
   */
  const { data: standing } = useQuery({
    ...trpc.diligences.standing.queryOptions({ id }),
    refetchInterval: collecting ? 15_000 : false
  })

  /** What the client chose to go without — it shows on the case, not only in the opinion. */
  const { data: waivers } = useQuery(trpc.diligences.waivers.queryOptions({ id }))

  /** How the chase for the counterparty's papers is going. */
  const { data: documentRequests } = useQuery(trpc.diligences.documentRequests.queryOptions({ id }))

  /** Whether anything was delivered — the acceptance is only asked about something that was. */
  const { data: deliveries } = useQuery(trpc.diligences.deliveries.queryOptions({ id }))

  /** The versions already kept, so the dialog knows what it is showing. */
  const { data: opinions, status: opinionsStatus } = useQuery(trpc.diligences.opinions.queryOptions({ id }))
  const opinionOpening = opinionOpeningOf({
    status: opinionsStatus,
    versions: opinions,
    writing: writeOpinion.isPending
  })

  /**
   * The version the dialog is about.
   *
   * The one the person picked, else the newest — which is the one just
   * written. A signed version is never replaced by a draft in the dossier, but
   * on screen the person is looking at what they asked to see.
   */
  const currentOpinion = opinions?.find(item => item.id === opinionId) ?? opinions?.[0] ?? null

  /** Where the case stands in the lawyer's review, as the server derives it from the opinions. */
  const { data: review } = useQuery(trpc.diligences.review.queryOptions({ id }))

  const overrideRisk = useMutation(
    trpc.diligences.overrideRisk.mutationOptions({
      onSuccess: async () => {
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: trpc.diligences.review.queryKey({ id }) }),
          queryClient.invalidateQueries({ queryKey: trpc.diligences.standing.queryKey({ id }) })
        ])
        toast.success('Gravidade revista e registrada no histórico.')
      },
      onError: error => toast.error(error.message)
    })
  )

  /** The client went back on a waiver: the document is owed again, and the record keeps both dates. */
  const revokeWaiver = useMutation(
    trpc.diligences.revokeWaiver.mutationOptions({
      onSuccess: async () => {
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: trpc.diligences.waivers.queryKey({ id }) }),
          queryClient.invalidateQueries({ queryKey: trpc.diligences.standing.queryKey({ id }) })
        ])
        toast.success('Dispensa revogada — o documento volta a ser exigido.')
      },
      onError: error => toast.error(error.message)
    })
  )

  const recordFinding = useMutation(
    trpc.diligences.recordManualFinding.mutationOptions({
      onSuccess: async () => {
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: trpc.diligences.review.queryKey({ id }) }),
          queryClient.invalidateQueries({ queryKey: trpc.diligences.standing.queryKey({ id }) }),
          queryClient.invalidateQueries({ queryKey: trpc.diligences.byId.queryKey({ id }) })
        ])
        toast.success('Achado registrado no caso.')
      },
      onError: error => toast.error(error.message)
    })
  )

  /** The lawyer's edit: a new version, which becomes the one on screen. */
  const reviseOpinion = useMutation(
    trpc.diligences.reviseOpinion.mutationOptions({
      onSuccess: async revised => {
        // The lawyer's version becomes the one on screen; no model was called,
        // so there is no usage to show for it.
        setOpinion(revised.text)
        setOpinionId(revised.id)
        setOpinionUsage(null)
        await queryClient.invalidateQueries({ queryKey: trpc.diligences.opinions.queryKey({ id }) })
        toast.success(`Versão ${revised.version} do parecer salva.`)
      },
      onError: error => toast.error(error.message)
    })
  )

  const signOpinion = useMutation(
    trpc.diligences.signOpinion.mutationOptions({
      onSuccess: async () => {
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: trpc.diligences.opinions.queryKey({ id }) }),
          // Signing is what releases the case: the stage and the queue move with it.
          queryClient.invalidateQueries({ queryKey: trpc.diligences.review.queryKey({ id }) }),
          queryClient.invalidateQueries({ queryKey: trpc.diligences.reviewQueue.queryKey() })
        ])
        toast.success('Parecer assinado.')
      },
      onError: error => toast.error(error.message)
    })
  )

  /**
   * What the whole diligence still has to be read.
   *
   * A document is on disk but the AI never got through it — the queue this
   * button drains. Certificates with no file are the scrapers' job.
   */
  const queue = useMemo(() => {
    if (!diligence) return []

    return diligence.subjects.flatMap(subject =>
      subject.certificates
        .filter(certificate => {
          if (!certificate.fileName) return false

          const status = visualStatusOf(certificate)

          return status === 'awaitingAi' || status === 'failed'
        })
        .map(certificate => ({ certificate, subject }))
    )
  }, [diligence])

  const running = useMemo(
    () =>
      (diligence?.subjects ?? []).flatMap(subject =>
        subject.certificates
          .filter(certificate => certificate.status === 'running')
          .map(certificate => ({ subject: subject.name, certificate: certificate.canonicalName ?? certificate.name }))
      ),
    [diligence]
  )

  /** What each document still out costs the case, per subject — the rows show it and sort by it. */
  const stakesBySubject = useMemo(() => {
    const bySubject = new Map<string, Map<string, AbsenceStake>>()

    for (const entry of standing?.stakes ?? []) {
      bySubject.set(entry.subjectId, (bySubject.get(entry.subjectId) ?? new Map()).set(entry.itemId, entry.stake))
    }

    return bySubject
  }, [standing?.stakes])

  /**
   * The subjects with something the queue could still be asked for.
   *
   * A bureau line with no declared purpose is left out: the server refuses it,
   * and what frees it is declaring the purpose — a next step of its own.
   */
  const searchable = useMemo(
    () =>
      (diligence?.subjects ?? [])
        .map(subject => ({
          subject,
          count: subject.certificates.filter(certificate => isSearchable(certificate) && !certificate.groundBlocked)
            .length
        }))
        .filter(item => item.count > 0),
    [diligence]
  )

  const reanalyzeAll = async () => {
    if (!queue.length) {
      toast.info('Nenhuma certidão aguardando leitura.')

      return
    }

    setBatch({ done: 0, total: queue.length, current: '' })

    for (const [index, item] of queue.entries()) {
      setBatch({
        done: index,
        total: queue.length,
        current: `${item.subject.name} — ${item.certificate.canonicalName ?? item.certificate.name}`
      })

      try {
        await reanalyzeCertificate.mutateAsync({ id: item.certificate.id })
      } catch {
        // The failure becomes the certificate's state; the queue moves on.
      }
    }

    await invalidate()
    setBatch(null)
    toast.success(`Leitura concluída: ${pluralize(queue.length, 'certidão', 'certidões')}.`)
  }

  /** Sends everything the case still owes to the search queue, subject by subject. */
  const searchAllPending = async () => {
    if (!searchable.length) {
      toast.info('Nenhuma certidão para buscar agora.')

      return
    }

    let scheduled = 0

    for (const { subject } of searchable) {
      try {
        scheduled += (await scrapeSubject.mutateAsync({ id: subject.id })).scheduled
      } catch (error) {
        toast.error(error instanceof Error ? error.message : 'Não foi possível enfileirar as buscas.')
      }
    }

    await invalidate()

    if (scheduled) toast.success(`${pluralize(scheduled, 'busca enfileirada', 'buscas enfileiradas')}.`)
  }

  /** Writes a new version — a model call, paid for, so only when asked. */
  const generateOpinion = () => {
    // One write at a time: the server refuses a second one too, but a click
    // it refuses is still a click that looked like it did something.
    if (writeOpinion.isPending) return setOpinionOpen(true)

    setOpinion('')
    setOpinionUsage(null)
    setOpinionOpen(true)
    writeOpinion.mutate({ id })
  }

  /**
   * Opens what is already kept, without calling the model (DIL-129): reading
   * the opinion again used to cost another one. Only a case whose list loaded
   * empty writes the first on opening — a list still loading, or one that
   * failed, writes nothing.
   */
  const openOpinion = () => {
    if (opinionOpening === 'wait') return
    // A version on its way: reopening shows its progress, never asks again.
    if (opinionOpening === 'watch') return setOpinionOpen(true)
    if (opinionOpening === 'write' || !opinions?.length) return generateOpinion()

    setOpinionId(opinions[0].id)
    setOpinionUsage(null)
    setOpinionOpen(true)
  }

  if (isPending || !diligence || !stats) {
    return (
      <Grid container spacing={6}>
        <Grid size={12}>
          <Skeleton variant='rounded' height={220} />
        </Grid>
        <Grid size={12}>
          <Skeleton variant='rounded' height={420} />
        </Grid>
      </Grid>
    )
  }

  const phase = casePhaseOf({ status: diligence.status, stage: review?.stage ?? null, stats })
  const tab: CaseTab = search.tab ?? TAB_OF_PHASE[phase]
  const purposeMissing = !diligence.legalPurpose.trim()
  const steps = nextStepsOf({ subjects: diligence.subjects, purposeMissing, stakes: standing?.stakes })
  const batchRunning = Boolean(batch)
  const searching = scrapeSubject.isPending
  const conclusive = standing ? standing.verdict !== 'inconclusive' : false
  const filter = search.states?.length ? new Set<VisualStatus>(search.states) : null
  const shownSubjects = search.subject
    ? diligence.subjects.filter(subject => subject.id === search.subject)
    : diligence.subjects
  const inView = (certificate: Certificate) =>
    (!filter || filter.has(visualStatusOf(certificate))) &&
    (!search.stage || collectionStageOf(certificate) === search.stage)
  const nothingInFilter =
    (filter !== null || Boolean(search.stage)) && !shownSubjects.some(subject => subject.certificates.some(inView))

  /** The measure the case is decided on — the documents its purpose demands — and the certificates as a fallback. */
  const progress =
    standing && standing.demanded > 0
      ? { done: standing.answered, total: standing.demanded, label: 'Documentos exigidos respondidos' }
      : { done: stats.resolved, total: stats.total, label: 'Certidões obtidas' }

  const showCertificates = (states: VisualStatus[]) => setSearch({ tab: 'certificates', states })

  const act = (kind: NextStepKind) => {
    if (kind === 'legalPurpose') return setSearch({ tab: 'settings' })
    if (kind === 'awaitingAi') return void reanalyzeAll()

    showCertificates([kind])

    if (kind === 'failed' || kind === 'notStarted') void searchAllPending()
  }

  const subjectSummary = (['cnpj', 'cpf', 'property'] as const)
    .map(type => {
      const count = diligence.subjects.filter(subject => subject.type === type).length
      const [one, many] = SUBJECT_COUNT_WORDS[type]

      return count ? pluralize(count, one, many) : null
    })
    .filter(Boolean)
    .join(' · ')

  /** The one action the phase calls for — the rest live in the menus beside it. */
  const primaryAction = (): ReactNode => {
    if (phase === 'collecting') {
      // The first next step is already the button that matters, in the same
      // blue: a second one here did the same thing under another name.
      if (steps.length) return null

      return (
        <Button
          variant='contained'
          startIcon={<i className='tabler-list-check' />}
          onClick={() => setSearch({ tab: 'certificates' })}
        >
          Ver certidões
        </Button>
      )
    }

    if (phase === 'analyzing') {
      return (
        <Button
          variant='contained'
          disabled={opinionOpening === 'wait'}
          startIcon={<i className='tabler-file-text' />}
          onClick={openOpinion}
        >
          {opinions?.length ? 'Abrir parecer' : 'Gerar parecer'}
        </Button>
      )
    }

    return (
      <Button
        variant='contained'
        startIcon={<i className='tabler-send' />}
        onClick={() => setSearch({ tab: 'delivery' })}
      >
        Entregar ao cliente
      </Button>
    )
  }

  return (
    <>
      <Grid container spacing={6}>
        <Grid size={12}>
          <Breadcrumbs className='mbe-4'>
            <RouterLink to='/' className='text-textSecondary no-underline hover:text-primary'>
              Início
            </RouterLink>
            <RouterLink to='/diligences' className='text-textSecondary no-underline hover:text-primary'>
              Diligências
            </RouterLink>
            <Typography color='text.primary'>{diligence.projectName}</Typography>
          </Breadcrumbs>

          <Card>
            <CardContent className='flex flex-col gap-5'>
              {/* Where the case is: its phase, its name, who is in it, and the decision when there is one. */}
              <div className='flex flex-wrap items-start justify-between gap-4'>
                <div className='flex-1 min-is-[280px]'>
                  <div className='flex flex-wrap items-center gap-2 mbe-2'>
                    <CustomChip
                      round='true'
                      size='small'
                      variant='tonal'
                      color={CASE_PHASE[phase].color}
                      label={`Fase: ${CASE_PHASE[phase].label}`}
                    />
                    {diligence.kind && (
                      <Tooltip arrow title='Tipo da operação'>
                        <CustomChip
                          round='true'
                          size='small'
                          variant='outlined'
                          color='secondary'
                          label={diligence.kind}
                        />
                      </Tooltip>
                    )}
                  </div>

                  <Typography variant='h4'>{diligence.projectName}</Typography>

                  <div className='flex flex-wrap items-center gap-4 mbs-2'>
                    {diligence.address && (
                      <Typography variant='body2' color='text.secondary' className='flex items-center gap-1'>
                        <i className='tabler-map-pin text-base' /> {diligence.address}
                      </Typography>
                    )}
                    <Typography variant='body2' color='text.secondary' className='flex items-center gap-1'>
                      <i className='tabler-users text-base' /> {subjectSummary || 'nenhum pesquisado'}
                    </Typography>
                    <Typography variant='body2' color='text.secondary' className='flex items-center gap-1'>
                      <i className='tabler-calendar text-base' /> aberto em {formatDate(diligence.createdAt)}
                    </Typography>
                  </div>
                </div>

                <CaseStatusControl
                  status={diligence.status}
                  conclusive={conclusive}
                  pending={updateStatus.isPending}
                  onChange={status => updateStatus.mutate({ id, status })}
                />
              </div>

              {/*
                One progress, the one the case is decided on. The certificates
                collected are a means; the documents the purpose demands are
                the measure — and the traffic light below reads the same one.
              */}
              <div className='flex flex-col gap-2'>
                <div className='flex flex-wrap items-baseline justify-between gap-2'>
                  <Typography variant='body1' className='font-medium'>
                    {progress.label}: {progress.done} de {progress.total}
                  </Typography>
                  {/* What moves on its own, said once — the steps below are only what waits on somebody. */}
                  {running.length > 0 && (
                    <Link
                      component='button'
                      type='button'
                      variant='caption'
                      onClick={() => showCertificates(['running'])}
                    >
                      <i className='tabler-loader-2 animate-spin text-sm align-middle' />{' '}
                      {pluralize(running.length, 'busca rodando', 'buscas rodando')} — o resultado aparece sozinho
                    </Link>
                  )}
                </div>
                <LinearProgress
                  variant='determinate'
                  value={percentOf(progress.done, progress.total)}
                  className='bs-[8px] rounded-full'
                  aria-label={progress.label}
                />
                <CaseBudgetLine budget={standing?.budget} />
              </div>

              {/* What the system is doing with each document — apart from what they found, right below. */}
              {collection.total > 0 && (
                <CollectionSummary
                  counts={collection.counts}
                  total={collection.total}
                  nextAttemptAt={collection.nextAttemptAt}
                  active={search.stage ?? null}
                  onSelect={stage => setSearch({ tab: 'certificates', stage })}
                />
              )}

              {/*
                The traffic light: the bar says how much was collected, and
                this says what that is worth. They are different questions and
                the second one is the one somebody decides on.
              */}
              {standing && (
                // The count of documents answered is the bar right above: not repeated here.
                <CaseVerdict verdict={standing.verdict} reasons={standing.reasons} risks={standing.risks} />
              )}

              {phase !== 'closed' && (
                <NextSteps
                  steps={steps}
                  running={running.length}
                  busy={[
                    ...(batchRunning ? (['awaitingAi'] as const) : []),
                    ...(searching ? (['failed', 'notStarted'] as const) : [])
                  ]}
                  onAct={act}
                  onCompleteData={group => {
                    const subject = diligence.subjects.find(item => item.id === group.subjectId)

                    if (subject) setEditing({ subject, fields: group.fields })
                  }}
                />
              )}

              {/* One action that matters now, and the rest grouped by intent. */}
              <div className='flex flex-wrap items-start gap-3'>
                {primaryAction()}

                <MenuButton
                  label='Adicionar'
                  icon='tabler-plus'
                  options={[
                    {
                      label: 'Pesquisado',
                      hint: 'Pessoa, empresa ou imóvel — cada um traz as suas certidões',
                      icon: 'tabler-user-plus',
                      onClick: () => setSubjectDialogOpen(true)
                    },
                    // Only where the purpose's matrix has papers of the deal (DIL-122).
                    ...(diligence.roles.includes('dealDocuments')
                      ? [
                          {
                            label: 'Pedido à contraparte',
                            hint: 'Documentos do negócio que só a outra parte tem',
                            icon: 'tabler-mail-forward',
                            onClick: () => setRequestOpen(true)
                          }
                        ]
                      : []),
                    {
                      label: 'Dispensa do cliente',
                      hint: 'O cliente decidiu seguir sem um documento',
                      icon: 'tabler-file-check',
                      onClick: () => setWaiverOpen(true)
                    }
                  ]}
                />

                {/*
                  The dossier stays a link of its own, with what goes in it
                  said beside it: it is the file the case exists to produce.
                */}
                <DossierDownloadButton diligenceId={id} counts={diligence.dossier} />

                <MenuButton
                  label='Relatórios'
                  icon='tabler-files'
                  options={[
                    {
                      label: 'Parecer',
                      hint: conclusive
                        ? 'Abre a versão guardada; gerar outra é pedido à parte'
                        : 'O caso ainda não conclui — o parecer sai sem conclusão',
                      icon: 'tabler-file-text',
                      disabled: opinionOpening === 'wait',
                      onClick: openOpinion
                    },
                    {
                      label: 'Planilha de validades',
                      hint: 'A validade de cada certidão, da que vence primeiro',
                      icon: 'tabler-table-export',
                      href: `/files/sheets/${id}`
                    },
                    ...(hasLinksReport(diligence.purpose)
                      ? [
                          {
                            label: 'Relatório de vínculos',
                            hint: 'Quadro societário e participações conhecidas',
                            icon: 'tabler-affiliate',
                            href: `/files/links/${id}`,
                            newTab: true
                          }
                        ]
                      : [])
                  ]}
                />
              </div>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={12}>
          <TabContext value={tab}>
            <CustomTabList
              variant='scrollable'
              scrollButtons='auto'
              onChange={(_, value: CaseTab) => setSearch({ tab: value })}
              className='mbe-5'
            >
              <Tab
                value='certificates'
                label={
                  <span className='flex items-center gap-2'>
                    Certidões ({stats.total})
                    {stats.blocked > 0 && (
                      <CustomChip
                        round='true'
                        size='small'
                        variant='tonal'
                        color='warning'
                        label={String(stats.blocked)}
                      />
                    )}
                  </span>
                }
              />
              <Tab value='analysis' label='Análise' />
              <Tab
                value='delivery'
                label={
                  <span className='flex items-center gap-2'>
                    Entrega
                    {review?.stage === 'released' && <i className='tabler-circle-check text-success text-base' />}
                  </span>
                }
              />
              <Tab
                value='settings'
                label={
                  <span className='flex items-center gap-2'>
                    Configuração
                    {purposeMissing && <i className='tabler-alert-circle text-warning text-base' />}
                  </span>
                }
              />
              <Tab value='log' label='Registro' />
            </CustomTabList>

            <TabPanel value='certificates' className='p-0 flex flex-col gap-6'>
              {diligence.subjects.length === 0 ? (
                <Card>
                  <CardContent className='flex flex-col items-center gap-4 plb-16 text-center'>
                    <CustomAvatar skin='light' color='primary' variant='rounded' size={52}>
                      <i className='tabler-users text-[28px]' />
                    </CustomAvatar>
                    <div>
                      <Typography variant='h5'>Nenhum pesquisado no caso</Typography>
                      <Typography color='text.secondary' className='max-is-[460px]'>
                        Adicione quem a diligência examina — pessoas, empresas e imóveis. Cada um traz o conjunto certo
                        de certidões.
                      </Typography>
                    </div>
                    <Button
                      variant='contained'
                      startIcon={<i className='tabler-plus' />}
                      onClick={() => setSubjectDialogOpen(true)}
                    >
                      Adicionar pesquisado
                    </Button>
                  </CardContent>
                </Card>
              ) : (
                <>
                  {/* The four stages first; the chips below narrow inside the one chosen. */}
                  <Tabs
                    value={search.stage ?? 'all'}
                    onChange={(_, value: string) =>
                      setSearch({ stage: value === 'all' ? undefined : (value as CollectionStage) })
                    }
                    variant='scrollable'
                    scrollButtons='auto'
                    aria-label='Situação da coleta'
                  >
                    <Tab value='all' label={`Todas (${collection.total})`} />
                    {COLLECTION_STAGES.map(stage => (
                      <Tab
                        key={stage}
                        value={stage}
                        label={`${COLLECTION_LOOK[stage].label} (${collection.counts[stage]})`}
                      />
                    ))}
                  </Tabs>

                  <CertificateFilters
                    subjects={diligence.subjects}
                    subject={search.subject ?? ''}
                    states={search.states ?? []}
                    counts={stats}
                    checkingMail={checkMailbox.isPending}
                    onSubject={subject => setSearch({ subject: subject || undefined })}
                    onStates={states => setSearch({ states: states.length ? states : undefined })}
                    onCheckMail={() => checkMailbox.mutate({ id })}
                  />

                  {shownSubjects.map(subject => (
                    <SubjectPanel
                      key={subject.id}
                      subject={subject}
                      filter={filter}
                      stage={search.stage ?? null}
                      batchRunning={batchRunning}
                      onEdit={target => setEditing({ subject: target, fields: [] })}
                      onAskForData={(target, certificate: Certificate) =>
                        setEditing({ subject: target, fields: certificate.missingFields })
                      }
                      stakes={stakesBySubject.get(subject.id)}
                    />
                  ))}

                  {nothingInFilter && (
                    <Typography color='text.secondary' className='text-center plb-8'>
                      Nenhuma certidão neste filtro.
                    </Typography>
                  )}
                </>
              )}

              {/* What no robot will bring, with the deadline and the script for getting it. */}
              <ManualQueuePanel
                diligenceId={id}
                canAssign={viewer?.role !== 'viewer' && viewer?.role !== 'auditor'}
              />

              {/* Triagem vira trabalho, não conclusão: a certidão é que é prova. */}
              <EvidenceGapsCard diligenceId={id} />

              <DocumentRequestList requests={documentRequests ?? []} />

              <WaiverList
                waivers={waivers ?? []}
                subjectNames={Object.fromEntries(diligence.subjects.map(subject => [subject.id, subject.name]))}
                canRevoke
                pending={revokeWaiver.isPending}
                onRevoke={input => revokeWaiver.mutate(input)}
              />
            </TabPanel>

            <TabPanel value='analysis' className='p-0 flex flex-col gap-6'>
              {review && (
                <Card>
                  <CardContent>
                    <LawyerReviewCard
                      stage={review.stage}
                      since={review.since}
                      risks={review.risks}
                      history={review.history}
                      canReview={viewer?.canSignOpinion ?? false}
                      pending={overrideRisk.isPending || recordFinding.isPending}
                      onOverride={input => overrideRisk.mutate({ id, ...input })}
                      manualRules={review.manualRules}
                      subjects={diligence.subjects.map(subject => ({ id: subject.id, name: subject.name }))}
                      canRecord={viewer?.role !== 'viewer' && viewer?.role !== 'auditor'}
                      onRecordFinding={input => recordFinding.mutate({ id, ...input })}
                    />
                  </CardContent>
                </Card>
              )}

              {/*
                What the matrícula says, field by field, with the score of each
                and the passage it was read from. It decides what the case owes.
              */}
              <MatriculaPanel diligenceId={id} subjects={diligence.subjects} />

              {/* A mesma leitura em campos, do outro documento que decide: o contrato social. */}
              <PowersPanel diligenceId={id} subjects={diligence.subjects} />
            </TabPanel>

            <TabPanel value='delivery' className='p-0 flex flex-col gap-6'>
              {/* Etapa 10: o lugar onde o cliente recebe, e o registro de que recebeu. */}
              <DeliveryCard diligenceId={id} />

              {/*
                t₄ só se mede sobre o que foi entregue: antes de um link, "foi
                aceito" e "voltou" seriam respostas sobre nada.
              */}
              {deliveries?.length ? (
                <DossierReviewCard diligenceId={id} />
              ) : (
                <Alert severity='info' icon={<i className='tabler-info-circle' />}>
                  O aceite do dossiê aparece aqui depois do primeiro link de entrega.
                </Alert>
              )}

              {/*
                O que mudou depois que alguém mediu o caso: indisponibilidade
                superveniente trava o registro no dia da lavratura.
              */}
              <MonitoringPanel diligenceId={id} expectedClosingOn={diligence.expectedClosingOn} />
            </TabPanel>

            <TabPanel value='settings' className='p-0 flex flex-col gap-6'>
              {/* Sob que termos o caso é medido: quem é o cliente muda o que a matriz exige. */}
              <CaseTermsCard
                diligenceId={id}
                clientProfile={diligence.clientProfile}
                expectedClosingOn={diligence.expectedClosingOn}
                budget={standing?.budget ?? null}
                canAuthorizeOverBudget={viewer?.role === 'admin'}
                elevatedByProfile={standing?.elevatedByProfile ?? []}
              />

              {/*
                Por que este caso pode consultar o CPF de quem nunca foi cliente.
                Fica no caso porque é quem abre o caso que escreve a finalidade.
              */}
              <LegalBasisCard
                diligenceId={id}
                basis={diligence.legalBasis}
                purpose={diligence.legalPurpose}
                casePurpose={diligence.purpose}
              />
            </TabPanel>

            <TabPanel value='log' className='p-0 flex flex-col gap-6'>
              {/* Quem pediu, de onde veio, quanto custou e o hash: o que faz do dossiê prova. */}
              <QueryLogCard diligenceId={id} />
            </TabPanel>
          </TabContext>
        </Grid>
      </Grid>

      <ScrapingIndicator items={running} batch={batch} />

      <SubjectDialog
        open={subjectDialogOpen}
        diligenceId={id}
        rolesByType={diligence?.rolesByType ?? NO_ROLES}
        onClose={() => setSubjectDialogOpen(false)}
      />

      <PersonalDataDialog
        open={Boolean(editing)}
        subject={editing?.subject ?? null}
        missingFields={editing?.fields ?? []}
        onClose={() => setEditing(null)}
      />

      <RequestDocumentsDialog open={requestOpen} diligenceId={id} onClose={() => setRequestOpen(false)} />

      <WaiverDialog
        open={waiverOpen}
        diligenceId={id}
        waivable={standing?.waivable ?? []}
        onClose={() => setWaiverOpen(false)}
      />

      <OpinionDialog
        open={opinionOpen}
        writing={writeOpinion.isPending}
        opinion={currentOpinion?.text ?? opinion}
        // What the model call cost is known only for the version just written;
        // opening a kept one or picking another clears it.
        usage={opinionUsage}
        current={currentOpinion}
        versions={opinions ?? []}
        onSelectVersion={version => {
          setOpinionId(version)
          setOpinionUsage(null)
        }}
        onGenerate={generateOpinion}
        // Quem assina é o advogado com inscrição registrada, não o
        // administrador: administrador é quem opera o sistema.
        canSign={viewer?.canSignOpinion ?? false}
        signing={signOpinion.isPending}
        onSign={note => currentOpinion && signOpinion.mutate({ opinionId: currentOpinion.id, note: note || undefined })}
        revising={reviseOpinion.isPending}
        // The version the edit was started on — not whichever is on screen.
        onRevise={(opinionId, text) => reviseOpinion.mutateAsync({ opinionId, text })}
        onClose={() => setOpinionOpen(false)}
      />
    </>
  )
}

type CertificateFiltersProps = {
  subjects: Subject[]
  subject: string
  states: VisualStatus[]
  counts: Record<VisualStatus, number>
  checkingMail: boolean
  onSubject: (subject: string) => void
  onStates: (states: VisualStatus[]) => void
  onCheckMail: () => void
}

/**
 * The list's filters: whose certificates, and in which state.
 *
 * The chips are the counts the case used to show as decoration, made into the
 * way to reach those certificates — "Faltam dados: 24" now opens the 24.
 * Only states with something in them are offered.
 */
function CertificateFilters({
  subjects,
  subject,
  states,
  counts,
  checkingMail,
  onSubject,
  onStates,
  onCheckMail
}: CertificateFiltersProps) {
  const toggle = (state: VisualStatus) =>
    onStates(states.includes(state) ? states.filter(item => item !== state) : [...states, state])

  return (
    <div className='flex flex-wrap items-center gap-3'>
      <CustomTextField
        select
        size='small'
        label='Pesquisado'
        value={subject}
        className='min-is-[220px]'
        onChange={event => onSubject(event.target.value)}
        slotProps={{ select: { displayEmpty: true }, inputLabel: { shrink: true } }}
      >
        <MenuItem value=''>Todos os pesquisados</MenuItem>
        {subjects.map(item => (
          <MenuItem key={item.id} value={item.id}>
            {item.name}
          </MenuItem>
        ))}
      </CustomTextField>

      <div className='flex flex-wrap items-center gap-2 flex-1' role='group' aria-label='Filtrar por estado'>
        {FILTER_ORDER.filter(state => counts[state] > 0).map(state => {
          const selected = states.includes(state)

          return (
            <CustomChip
              key={state}
              round='true'
              size='small'
              variant={selected ? 'filled' : 'tonal'}
              color={VISUAL_STATUS[state].color}
              label={`${VISUAL_STATUS[state].label}: ${counts[state]}`}
              aria-pressed={selected}
              onClick={() => toggle(state)}
            />
          )
        })}
        {states.length > 0 && (
          <Button size='small' variant='text' color='secondary' onClick={() => onStates([])}>
            Limpar filtro
          </Button>
        )}
      </div>

      {counts.awaitingEmail > 0 && (
        <Tooltip arrow title='O sistema confere sozinho; isto confere agora.'>
          <Button
            size='small'
            variant='tonal'
            color='info'
            disabled={checkingMail}
            startIcon={<i className={checkingMail ? 'tabler-loader-2 animate-spin' : 'tabler-mail-search'} />}
            onClick={onCheckMail}
          >
            Conferir e-mail agora
          </Button>
        </Tooltip>
      )}
    </div>
  )
}
